import { type Vector3 } from '@canvas/vector';

import {
  lightColorMap,
  mediumColorMap,
  strongColorMap,
  strongestColorMap,
  textColorMap,
  iconColorMap,
  backgroundColorMap,
  outlineColorMap,
} from './color-maps';
import {
  accentForLightColorMap,
  accentForMediumColorMap,
  accentForStrongColorMap,
} from './sticky';

export const ColorPaletteKeys = [
  'light',
  'medium',
  'strong',
  'strongest',
  'text',
  'icon',
  'background',
  'outline',
] as const;

export type ColorPaletteKey = (typeof ColorPaletteKeys)[number];

// Defines a dictionary of the available palettes by key and the actual mapping
// of colour identifier -> vec3 (rgb)
// When adding a new palette for consumption, make sure you add it here too or
// TS will complain ;)
export const paletteColourMapping: Record<ColorPaletteKey, Record<string, Vector3>> = {
  ['light']: lightColorMap,
  ['medium']: mediumColorMap,
  ['strong']: strongColorMap,
  ['strongest']: strongestColorMap,
  ['text']: textColorMap,
  ['icon']: iconColorMap,
  ['background']: backgroundColorMap,
  ['outline']: outlineColorMap,
} as const;

// Maps the set of sticky background colours to its accent colours
export const stickyAccentColourMapping: Record<string, Record<string, string>> = {
  light: accentForLightColorMap,
  medium: accentForMediumColorMap,
  strong: accentForStrongColorMap,
};

// We define palettes as the available colour options for a specific type
// of entities. For example paths can only contain colours from the TEXT, ICON
// and BACKGROUND palettes. This tight mapping prevents us from adding/removing
// colours that will result in breakages and IF a palette is not here, the colour
// CANNOT be rendered on that entity
export const allPathColourPalette = [
  // NEW ADS 2.0
  'text',
  'icon',
  'background',

  // OLD - Will need a WDF migration to remove but we need these for backwards compatibility
  'strongest',
  'strong',
  'medium',
] as const;

// Available palettes that can be rendered on a stickies and shapes in the foreground
export const allDefaultColourPalette = ['light', 'medium', 'strong'] as const;

/**
 * Strongly typed tuple here to enforce that you can ONLY ever add entries that belong to the palette
 * declared in T. Currently limited to 3 (by design) but could be extended in the future
 */
type ColorSwatch<T extends readonly ColorPaletteKey[]> = readonly [
  T[number],
  T[number],
  T[number]
];

/**
 * A colour swatch is what we show in the colour picker and it is an ordered
 * array of palette keys which will each represent a single row in the picker.
 */
export const backgroundColourPaletteSwatch: ColorSwatch<typeof allDefaultColourPalette> =
  [
    // NOTE: Ordering here determines the order they render in the colour picker
    'light',
    'medium',
    'strong',
  ] as const;
export const strokeColourPaletteSwatch: ColorSwatch<typeof allPathColourPalette> = [
  // NOTE: Ordering here determines the order they render in the colour picker
  'text',
  'icon',
  'background',
] as const;

/**
 * A swatch could be defined as a set of palettes which can be used as a colour for an entity. This type
 * is here to help restrict the typing to avoid problems in the future
 */
export type ColorSwatches =
  | typeof backgroundColourPaletteSwatch
  | typeof strokeColourPaletteSwatch;
